import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Crown,
  Check,
  ArrowLeft,
  CreditCard,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { CompanionMembershipPlan } from '../types';
import { auth } from '../lib/firebase';
import { RazorpayCheckout, RazorpayCheckoutResponse } from './RazorpayCheckout';
import {
  createPaymentOrder,
  verifyPaymentWithBackend,
  checkPaymentStatus,
  StandardPaymentStatus,
} from '../lib/paymentService';

interface MembershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  companionName?: string;
  onPlanActivated: (plan: CompanionMembershipPlan, amount: number) => void;
  onGoToDashboard?: () => void;
  onCompleteProfile?: () => void;
}

export const MembershipModal: React.FC<MembershipModalProps> = ({
  isOpen,
  onClose,
  companionName = 'Companion Host',
  onPlanActivated,
  onGoToDashboard,
  onCompleteProfile,
}) => {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<
    'plans' | 'checkout' | 'success' | 'failed' | 'pending' | 'cancelled'
  >('plans');

  const [selectedPlan, setSelectedPlan] = useState<{
    id: CompanionMembershipPlan;
    name: string;
    price: number;
    period: string;
  }>({
    id: 'PROFESSIONAL',
    name: 'PROFESSIONAL',
    price: 1099,
    period: '3 Months',
  });

  const [orderId, setOrderId] = useState<string>('');
  const [checkoutKeyId, setCheckoutKeyId] = useState<string>('');
  const [checkoutAmountPaise, setCheckoutAmountPaise] = useState<number>(0);
  const [membershipId, setMembershipId] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<StandardPaymentStatus>('CREATED');
  const [isProcessing, setIsProcessing] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string>('');

  // Calculate next billing date
  const getNextBillingDate = () => {
    const d = new Date();

    if (selectedPlan.period === '1 Year') {
      d.setFullYear(d.getFullYear() + 1);
    } else if (selectedPlan.period === '3 Months') {
      d.setMonth(d.getMonth() + 3);
    } else {
      d.setMonth(d.getMonth() + 1);
    }

    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const nextBillingDate = getNextBillingDate();

  if (!isOpen) return null;

  const plans = [
    {
      id: 'STARTER' as CompanionMembershipPlan,
      name: 'STARTER',
      price: 449,
      period: '1 Month',
      popular: false,
      buttonText: 'Choose Starter',
      features: [
        'Standard profile listing in search',
        'Receive up to 5 bookings/week',
        'Standard Escrow payout (24h)',
        'Basic in-app messaging',
      ],
    },
    {
      id: 'PROFESSIONAL' as CompanionMembershipPlan,
      name: 'PROFESSIONAL',
      price: 1099,
      period: '3 Months',
      popular: true,
      buttonText: 'Choose Professional',
      features: [
        'Priority search listing badge',
        'Unlimited bookings per week',
        'Instant OTP Escrow payout to bank',
        'Verified Companion Shield',
        'Direct phone & WhatsApp leads',
      ],
    },
    {
      id: 'PREMIUM' as CompanionMembershipPlan,
      name: 'PREMIUM',
      price: 5999,
      period: '1 Year',
      popular: false,
      buttonText: 'Choose Premium',
      features: [
        'Top of page VIP spotlight',
        '0% platform commission',
        'Dedicated 24/7 account manager',
        'VIP Events & High-Profile Dinners',
        'Maximum hourly rate flexibility',
      ],
    },
  ];

  // Create a real backend order before opening Razorpay Checkout.
  const handleSelectPlan = async (plan: typeof plans[0]) => {
    setIsProcessing(true);
    setFailureMessage('');
    setOrderId('');
    setCheckoutKeyId('');
    setCheckoutAmountPaise(0);
    setSelectedPlan({
      id: plan.id, name: plan.name, price: plan.price, period: plan.period,
    });

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in before purchasing membership.');
      }

      const res = await createPaymentOrder({
        companionId: user.uid,
        userId: user.uid,
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        paymentMethod: 'RAZORPAY_CHECKOUT',
      });

      if (
        res.success !== true ||
        !res.order_id ||
        !res.keyId ||
        !Number.isSafeInteger(res.amount) ||
        res.amount <= 0
      ) {
        throw new Error('Backend did not return a valid Razorpay order, key, and amount.');
      }

      setOrderId(res.order_id);
      setCheckoutKeyId(res.keyId);
      setCheckoutAmountPaise(res.amount);
      setMembershipId(res.membership_id || '');
      setPaymentStatus('CREATED');
      setSelectedPlan({
        id: plan.id,
        name: res.plan_name || plan.name,
        price: res.amount / 100,
        period: res.plan_duration || plan.period,
      });
      setViewState('checkout');
    } catch (error) {
      setFailureMessage(
        error instanceof Error ? error.message : 'Could not create a secure payment order.'
      );
      setPaymentStatus('FAILED');
      setViewState('failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // Only verify the actual Razorpay Checkout response.
  const handleVerify = async (response: RazorpayCheckoutResponse) => {
    setIsProcessing(true);
    setFailureMessage('');

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Session expired. Please sign in again.');

      if (
        !orderId ||
        response.razorpay_order_id !== orderId ||
        !response.razorpay_payment_id ||
        !response.razorpay_signature
      ) {
        throw new Error('Incomplete or mismatched Razorpay payment response.');
      }

      const res = await verifyPaymentWithBackend({
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        signature: response.razorpay_signature,
        companionId: user.uid,
        userId: user.uid,
        planId: selectedPlan.id,
        paymentMethod: 'RAZORPAY_CHECKOUT',
      });

      if (
        res.success === true &&
        res.status === 'SUCCESS' &&
        res.payment_verified === true &&
        res.webhook_verified === 'YES' &&
        Boolean(res.payment_id)
      ) {
        setPaymentStatus('SUCCESS');
        onPlanActivated(selectedPlan.id, selectedPlan.price);
        setViewState('success');

        setTimeout(() => {
          onClose();
          navigate('/payment-success', {
            replace: true,
            state: {
              orderId: res.order_id || orderId,
              paymentId: res.payment_id,
              membershipId: res.membership_id || membershipId,
              planName: res.plan_name || selectedPlan.name,
              amount: typeof res.amount === 'number'
                ? res.amount / 100
                : selectedPlan.price,
              targetNext: '/dashboard',
            },
          });
        }, 1500);
        return;
      }

      if (res.status === 'PENDING') {
        setPaymentStatus('PENDING');
        setFailureMessage(
          res.message || 'Payment is awaiting server confirmation. Do not pay again yet.'
        );
        setViewState('pending');
        return;
      }

      setPaymentStatus('FAILED');
      setFailureMessage(
        res.error || res.message ||
        'Payment is not server-confirmed. Membership remains inactive.'
      );
      setViewState('failed');
    } catch (error) {
      setPaymentStatus('FAILED');
      setFailureMessage(
        error instanceof Error
          ? error.message
          : 'Verification failed. Membership remains inactive.'
      );
      setViewState('failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in">
      <div className="relative bg-white rounded-3xl max-w-4xl w-full overflow-hidden shadow-2xl border border-purple-100 p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={() => {
            if (viewState === 'checkout') {
              setViewState('cancelled');
            } else {
              onClose();
            }
          }}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors z-10 cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* 1. PLANS SELECTION */}
        {viewState === 'plans' && (
          <div className="space-y-6">
            <div className="text-center max-w-xl mx-auto">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3">
                <Crown className="w-7 h-7" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900">
                Choose Your Companion Membership
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 mt-1.5">
                A verified membership plan is required to activate your Companion profile and start accepting bookings.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-3xl p-6 flex flex-col justify-between border transition-all ${
                    plan.popular
                      ? 'border-purple-600 bg-gradient-to-b from-purple-50/60 via-white to-pink-50/40 shadow-xl relative scale-102'
                      : 'border-slate-200 bg-white hover:border-slate-300 shadow-2xs'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-black tracking-wider uppercase px-3 py-0.5 rounded-full shadow-md">
                      ★ MOST POPULAR
                    </div>
                  )}

                  <div>
                    <h4 className="font-extrabold text-slate-900 text-lg mb-1 tracking-tight">
                      {plan.name}
                    </h4>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-3xl font-black text-slate-900">₹{plan.price}</span>
                      <span className="text-xs text-slate-500">/{plan.period}</span>
                    </div>

                    <div className="space-y-2.5 mb-6 text-xs text-slate-600">
                      {plan.features.map((feat, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={isProcessing}
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full py-3 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {plan.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 2. PAYMENT CHECKOUT (RULE 1 OF SPECIFICATION) */}
        {viewState === 'checkout' && (
          <div className="max-w-lg mx-auto space-y-5">
            <button
              onClick={() => setViewState('plans')}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" /> Change Plan
            </button>

            <div className="text-center">
              <span className="text-xs font-bold text-purple-600 uppercase tracking-wider">
                Payment Gateway Checkout
              </span>
              <h3 className="text-2xl font-black text-slate-900 mt-1">Payment Verification</h3>
            </div>

            {/* Payment Page required information */}
            <div className="p-4 rounded-2xl bg-purple-50/70 border border-purple-200 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-purple-700">Membership Name</span>
                  <h4 className="text-base font-black text-slate-900">{selectedPlan.name}</h4>
                </div>
                <div className="text-right">
                  <span className="text-[10px] uppercase font-bold text-purple-700">Amount</span>
                  <span className="text-2xl font-black text-slate-900">₹{selectedPlan.price}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-purple-200/80 text-[11px] text-slate-600">
                <div>
                  <span>Duration:</span>
                  <strong className="block font-bold text-slate-800">{selectedPlan.period}</strong>
                </div>
                <div>
                  <span>Order ID:</span>
                  <strong className="block font-mono text-slate-800">{orderId || 'Generating...'}</strong>
                </div>
                <div className="text-right">
                  <span>Status:</span>
                  <span className="inline-block px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-black">
                    {paymentStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* Real Razorpay Checkout; no direct UPI QR or fake card fields. */}
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              {failureMessage && (
                <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                  {failureMessage}
                </p>
              )}

              {orderId && checkoutKeyId && checkoutAmountPaise > 0 ? (
                <RazorpayCheckout
                  orderId={orderId}
                  keyId={checkoutKeyId}
                  amountPaise={checkoutAmountPaise}
                  description={`${selectedPlan.name} membership`}
                  customerName={companionName}
                  customerEmail={auth.currentUser?.email || undefined}
                  onPaymentResponse={handleVerify}
                  onDismiss={() => setFailureMessage(
                    'Checkout was closed. No payment success has been recorded.'
                  )}
                  onError={(message) => setFailureMessage(message)}
                />
              ) : (
                <p role="alert" className="text-sm text-rose-700">
                  Secure checkout unavailable: a valid backend order was not created.
                  Go back and try again.
                </p>
              )}

              <p className="text-xs leading-relaxed text-slate-500">
                Membership is activated only after server-side payment verification
                and webhook confirmation.
              </p>
            </div>
          </div>
        )}

        {/* 3. PAYMENT SUCCESSFUL (RULE 2) */}
        {viewState === 'success' && (
          <div className="max-w-md mx-auto text-center space-y-4 py-4 animate-in zoom-in-95">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <div>
              <h3 className="text-2xl font-black text-slate-900">✓ Payment Successful</h3>
              <h4 className="text-base font-bold text-emerald-600 mt-0.5">Membership Activated</h4>
              <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Redirecting automatically...
              </p>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-left text-xs space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Plan:</span>
                <span className="font-extrabold text-slate-900">{selectedPlan.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Status:</span>
                <span className="font-bold text-emerald-600">🟢 ACTIVE</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Order ID:</span>
                <span className="font-mono text-slate-800">{orderId}</span>
              </div>
            </div>
          </div>
        )}

        {/* 4. PAYMENT FAILED (RULE 3) */}
        {viewState === 'failed' && (
          <div className="max-w-md mx-auto text-center space-y-5 py-4 animate-in fade-in">
            <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8" />
            </div>

            <div>
              <h3 className="text-2xl font-black text-slate-900">✕ Payment Failed</h3>
              <p className="text-xs sm:text-sm font-semibold text-rose-700 mt-1">
                Your payment could not be completed.
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {failureMessage || 'Transaction was declined by bank.'}
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <button
                type="button"
                onClick={() => setViewState('checkout')}
                className="w-full py-3 bg-purple-700 hover:bg-purple-800 text-white font-extrabold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                [Try Again]
              </button>
              <button
                type="button"
                onClick={() => setViewState('plans')}
                className="w-full py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 cursor-pointer"
              >
                Change Membership Plan
              </button>
            </div>
          </div>
        )}

        {/* 5. PAYMENT PENDING (RULE 4) */}
        {viewState === 'pending' && (
          <div className="max-w-md mx-auto text-center space-y-5 py-4 animate-in fade-in">
            <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <Clock className="w-8 h-8 animate-pulse" />
            </div>

            <div>
              <h3 className="text-2xl font-black text-slate-900">⏳ Payment Pending</h3>
              <p className="text-xs sm:text-sm font-semibold text-amber-800 mt-1">
                We are verifying your payment.
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Please do not make another payment.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <button
                type="button"
                disabled={isProcessing}
                onClick={async () => {
                  setIsProcessing(true);
                  const st = await checkPaymentStatus(orderId);
                  setIsProcessing(false);
                  if (st && st.status === 'SUCCESS' && st.webhook_verified === 'YES') {
                    setPaymentStatus('SUCCESS');
                    onPlanActivated(selectedPlan.id, selectedPlan.price);
                    setViewState('success');
                    onClose();
                    navigate('/payment-success', {
                      state: {
                        planName: selectedPlan.name,
                        planId: selectedPlan.id,
                        amount: selectedPlan.price,
                        duration: selectedPlan.period,
                        orderId: st.order_id || orderId,
                        paymentId: st.payment_id || '',
                        membershipId: st.membership_id || '',
                        paymentMethod: st.payment_method || 'Razorpay Verified',
                      },
                    });
                  } else {
                    alert('Status is still PENDING with bank.');
                  }
                }}
                className="w-full py-3 bg-purple-700 hover:bg-purple-800 text-white font-extrabold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                [Check Payment Status]
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2 text-xs font-bold text-slate-500 hover:text-slate-800 cursor-pointer"
              >
                Close and Check Later
              </button>
            </div>
          </div>
        )}

        {/* 6. CANCELLED CHECKOUT */}
        {viewState === 'cancelled' && (
          <div className="max-w-md mx-auto text-center space-y-5 py-4 animate-in fade-in">
            <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <Crown className="w-8 h-8" />
            </div>

            <div>
              <h3 className="text-2xl font-black text-slate-900">Complete Your Membership</h3>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                Your membership hasn't been activated yet. Choose a membership plan to start accepting bookings and earning money.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <button
                type="button"
                onClick={() => setViewState('plans')}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
              >
                View Membership Plans
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Decide Later
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
